import {
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  HostListener, inject,
  input,
  model,
  OnInit,
  output,
  Signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {isDefined} from "../../../../core/util/isDefined";
import {translate} from "../../../translate/translate";

type InputValueTypes = string | number;

type InputOutputEvent = { value: InputValueTypes, target: HTMLInputElement };

@Component({
  selector: 'app-input',
  imports: [NgTemplateOutlet, ReactiveFormsModule, NgClass, FormsModule],
  templateUrl: './input.component.html',
})
export class InputComponent implements OnInit {
  elementRef: ElementRef = inject(ElementRef);

  /**
   * Placeholder-teksti, jos ei ole asetettu
   */
  defaultPlaceHolderKey = translate('global.input.placeholder');

  /**
   * Placeholder dynaamisena input-signaalina
   */
  placeholder = input<string>();

  /**
   * the real string of placeholder text
   */
  placeholderText = computed(() => this.placeholder() ?? this.defaultPlaceHolderKey());

  /**
   * Disabled-tila signaalina
   */
  disabled = input<boolean>(false);

  /**
   * Alustava (initial) arvo
   */
  initialValue = input<InputValueTypes | null>(null);

  /**
   * FormControl arvo yliajaa linkedValue:n jos input kenttää halutaan käyttää formControllerin kautta
   */
  formController = input<FormControl<string|number>>();

  /**
   * Arvo, joka on kytketty ulkoiseen malliin
   */
  linkedValue = model<InputValueTypes>();

  /**
   * Input type
   */
  type = input<string>('text');

  /**
   * Lähetetään ulospäin aina, kun arvo lukitaan (blur)
   */
  valueChange = output<InputOutputEvent>();

  /**
   * Lähetetään ulospäin aina, kun `inputValue` muuttuu
   */
  valueInput = output<InputOutputEvent>()

  /**
   * Mahdollinen yksikkö (esim. "mm", "kg" tms.), näytetään inputin perässä
   */
  unit = input<string>();

  /**
   * Input-kentän padding
   */
  inputPadding = input<number>(2);
  inputPaddingStyle= computed(() => this.inputPaddingStyles[this.inputPadding()] ?? '' );
  inputPaddingStyles: {[size:number]: string} = {
    0: 'p-0',
    1: 'p-1',
    2: 'p-2',
    3: 'p-3',
    4: 'p-4'
  }

  /**
   * reference for input element
   */
  inputElement: Signal<ElementRef<HTMLInputElement>> = viewChild('inputElement');

  /**
   * Valmius custom contentille, jos halutaan esim. lisäikoni input-kentän sisään
   */
  customContent: Signal<TemplateRef<any>> = contentChild('customContent');

  constructor() {
    this.onInputChange();
    this.handleFormControllerDisabling();
  }

  ngOnInit(): void {
    if(this.formController() && this.linkedValue()) { console.warn('InputComponent: formController overrides linkedValue.'); }
    //if(!this.formController() && !this.linkedValue()) { console.debug('InputComponent: neither controls; formController or linkedValue is given!'); }

    // Jos komponentille on asetettu initialValue, käytetään sitä
    if (isDefined(this.initialValue())) { this.linkedValue.set(this.initialValue()); }
    if (isDefined(this.formController())) { this.formController().setValue(this.initialValue()); }
  }

  /**
   * Esimerkki: enter-näppäin tai vastaava
   */
  @HostListener('keydown.enter', ['$event'])
  onEnter(event: KeyboardEvent) {

    event.preventDefault();
    event.stopPropagation();
    this.inputElement().nativeElement.blur();

    // Voit halutessasi käsitellä Enter-näppäimen painalluksen logiikkaa
    //console.log('Enter painettu, arvo:', this.formController() ? this.formController().value : this.linkedValue());
  }

  /**
   * Estää default-toiminnallisuuden custom elementissä. yliajetaan omilla output
   */
  @HostListener('change', ['$event'])
  @HostListener('input', ['$event'])
  onChanges(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * sends data on commit (blur)
   */
  onValueChange() {
    this.valueChange.emit({value: this.linkedValue(), target: this.inputElement().nativeElement});
  }

  /**
   * sends data on input
   */
  onInputChange(){
    effect(() => this.linkedValue() && this.inputElement() && this.valueInput.emit({value: this.linkedValue(), target: this.inputElement().nativeElement}));
    effect(() => this.formController() && this.inputElement() && this.valueInput.emit({value: this.formController().value, target: this.inputElement().nativeElement}));
  }

  /**
   * Disables formController if disabled
   * @private
   */
  private handleFormControllerDisabling(){
    // Disable formController if disabled
    effect(() => {
      if(this.disabled() && this.formController()) this.formController().disable();
      if(!this.disabled() && this.formController()) this.formController().enable();
    });
  }

}
